import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, User, Mail, Lock, Check, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  signUp,
  signIn,
  resetPassword,
  resendConfirmation,
  sendLoginCode,
  confirmWithCode,
  emailExists,
  checkUsernameAvailable,
  isValidEmail,
  passwordProblem,
} from '@/lib/auth/auth'
import { signInWithOAuth } from '@/integrations/coderblock/auth'
import { useAuthUi } from '@/lib/auth/authUi'

const SIGNUP_DISABLED = false
const SIGNUP_DISABLED_MESSAGE = 'Profiles are currently being wrenched on. Check back soon!'

// Mirrors the format rule enforced by the database (claim_username / username_available).
const USERNAME_RE = /^[A-Za-z0-9_]{3,24}$/

interface AuthModalProps {
  open: boolean
  onClose: () => void
  /** 'panel' = opened from the account panel, 'save' = user hit Save Route */
  reason: 'panel' | 'save' | 'suggestion'
}

type Mode = 'signin' | 'signup' | 'forgot' | 'confirm-code'

export default function AuthModal({ open, onClose, reason }: AuthModalProps) {
  const [mode, setMode] = useState<Mode>('signin')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [code, setCode] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [resendBusy, setResendBusy] = useState(false)
  const [resendDone, setResendDone] = useState(false)
  const [busy, setBusy] = useState(false)
  const [googleBusy, setGoogleBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [usernameState, setUsernameState] = useState<'idle' | 'ok' | 'taken' | 'check-failed'>(
    'idle'
  )
  const [emailState, setEmailState] = useState<'idle' | 'ok' | 'taken'>('idle')
  const googleCancelRef = useRef<(() => void) | null>(null)
  const reopenSuggest = useAuthUi((s) => s.reopenSuggest)
  const fromSuggestion = reason === 'suggestion'

  if (!open) return null

  const reset = () => {
    setUsername('')
    setEmailState('idle')
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setCode('')
    setConfirmEmail('')
    setCodeSent(false)
    setResendBusy(false)
    setResendDone(false)
    setError(null)
    setNotice(null)
    setUsernameState('idle')
    setMode('signin')
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  async function handleEmailBlur() {
    const addr = email.trim()
    if (mode !== 'signup' || !isValidEmail(addr)) {
      setEmailState('idle')
      return
    }
    setEmailState('idle')
    const taken = await emailExists(addr)
    setEmailState(taken === false ? 'ok' : taken === true ? 'taken' : 'idle')
  }

  async function handleUsernameBlur() {
    const name = username.trim()
    if (mode !== 'signup' || !USERNAME_RE.test(name)) {
      setUsernameState('idle')
      return
    }
    setUsernameState('idle')
    const available = await checkUsernameAvailable(name)
    setUsernameState(available ? 'ok' : 'taken')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setNotice(null)

    if (mode === 'forgot') {
      if (!isValidEmail(email)) return setError('Please enter a valid email address.')
      setBusy(true)
      const exists = await emailExists(email)
      if (exists === false) {
        setBusy(false)
        return setError('No account found with that email address.')
      }
      if (exists === null) {
        // Lookup unavailable — fall back to Supabase's default behavior.
        const { error: err } = await resetPassword(email)
        setBusy(false)
        if (err) return setError(err)
        setNotice('Remote login link sent — check your inbox (and spam folder). Open it on this device to set a new password.')
        return
      }
      const { error: err } = await resetPassword(email)
      setBusy(false)
      if (err) return setError(err)
      setNotice('Remote login link sent — check your inbox (and spam folder). Open it on this device to set a new password.')
      return
    }

    if (mode === 'confirm-code') {
      const mail = confirmEmail.trim()
      if (!isValidEmail(mail)) return setError('Please enter a valid email address.')
      if (!codeSent) {
        setBusy(true)
        const { error: err } = await sendLoginCode(mail)
        setBusy(false)
        if (err) return setError(err)
        setCodeSent(true)
        setNotice(`Code sent to ${mail} — it expires in 15 minutes.`)
        return
      }
      if (!code.trim()) return setError('Enter the 6-character code from your email.')
      setBusy(true)
      const { error: err } = await confirmWithCode(mail, code)
      setBusy(false)
      if (err) return setError(err)
      if (fromSuggestion) reopenSuggest()
      handleClose()
      return
    }

    if (mode === 'signup') {
      if (SIGNUP_DISABLED) return setError(SIGNUP_DISABLED_MESSAGE)
      const name = username.trim()
      if (!USERNAME_RE.test(name)) {
        return setError(
          'Username must be 3–24 characters — letters, numbers and underscores only.'
        )
      }
      if (!isValidEmail(email)) return setError('Please enter a valid email address.')
      const pw = passwordProblem(password)
      if (pw) return setError(pw)
      if (password !== confirmPassword) return setError('Passwords do not match.')

      setBusy(true)
      const available = await checkUsernameAvailable(name)
      if (!available) {
        setBusy(false)
        return setError('That username is already taken.')
      }
      // The signup endpoint deliberately returns a fake success for an
      // already-registered email (anti-enumeration), so check first.
      if (emailState === 'taken') {
        setBusy(false)
        return setError('That email is already in use — try signing in instead.')
      }
      const emailTaken = emailState !== 'ok' ? await emailExists(email) : false
      if (emailTaken) {
        setBusy(false)
        return setError('That email is already in use — try signing in instead.')
      }
      const { error: err } = await signUp({ username: name, email, password })
      setBusy(false)
      if (err) return setError(err)
      setNotice('Check your email to confirm your account, then sign in.')
      setConfirmPassword('')
      setMode('signin')
      return
    }

    // sign in
    setBusy(true)
    const { error: err } = await signIn(username, password)
    setBusy(false)
    if (err) return setError(err)
    if (fromSuggestion) reopenSuggest()
    handleClose()
  }

  async function handleGoogle() {
    setError(null)
    setNotice(null)
    setGoogleBusy(true)
    googleCancelRef.current = signInWithOAuth('google', {
      onComplete: (result) => {
        googleCancelRef.current = null
        setGoogleBusy(false)
        if (result.ok) {
          if (fromSuggestion) reopenSuggest()
          handleClose()
        } else if (result.error === 'cancelled') {
          // User closed the popup / tapped the button again — back to a fresh,
          // enabled Google button. No error message.
        } else if (result.error === 'timeout') {
          setError('Google sign-in timed out — please try again.')
        } else {
          setError('Google sign-in failed. Please try again.')
        }
      },
    })
  }

  async function handleResend() {
    const mail = (mode === 'confirm-code' ? confirmEmail : username).trim()
    if (!isValidEmail(mail)) {
      setError('Enter your email address first, then resend.')
      return
    }
    setError(null)
    setResendBusy(true)
    const { error: err } = mode === 'confirm-code' && codeSent
      ? await sendLoginCode(mail)
      : await resendConfirmation(mail)
    setResendBusy(false)
    if (err) return setError(err)
    setResendDone(true)
  }

  function handleGoogleButton() {
    if (googleBusy) {
      // Second tap while connecting: cancel the in-flight attempt and reset.
      googleCancelRef.current?.()
      return
    }
    void handleGoogle()
  }

  // Render at document.body level: the parent UI container sits at a lower
  // stacking level than the route tray, so the modal (despite its high
  // z-index) animated BEHIND "Your Route" on mobile. A portal escapes that
  // ancestor stacking context entirely.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0e0e12] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {mode === 'signin'
                ? 'Welcome back'
                : mode === 'signup'
                  ? 'Create your account'
                  : mode === 'confirm-code'
                    ? 'Confirm your email'
                    : 'Remote login link'}
            </h2>
            <p className="mt-1 text-sm text-white/50">
              {mode === 'signin'
                ? 'Sign in with your username or email.'
                : mode === 'signup'
                  ? 'Save routes to your account across devices.'
                    : mode === 'confirm-code'
                      ? codeSent
                        ? 'Enter the 6-character code we just emailed you.'
                        : 'Enter your email and we\u2019ll send a 6-character confirmation code \u2014 no link-clicking needed.'
                    : 'Enter your account email and we\u2019ll send you a remote login link. Opening it on a device signs you in so you can set a new password.'}
            </p>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {reason === 'save' && (
          <div className="mb-4 rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
            {SIGNUP_DISABLED
              ? 'Sign in to save this route.'
              : 'Sign in or create an account to save this route.'}
          </div>
        )}

        {reason === 'suggestion' && (
          <div className="mb-4 rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
            Log in to include your road suggestion in your account stats.
          </div>
        )}

        {SIGNUP_DISABLED && mode === 'signup' ? (
          <div className="rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
            {SIGNUP_DISABLED_MESSAGE}
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'confirm-code' ? (
            <>
              <label className="block">
                <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                  <Mail className="h-3.5 w-3.5" /> Email
                </span>
                <input
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60"
                />
              </label>
              {codeSent && (
                <label className="block">
                  <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                    <Lock className="h-3.5 w-3.5" /> Confirmation code
                  </span>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="6-character code"
                    maxLength={6}
                    autoComplete="one-time-code"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm font-mono tracking-widest uppercase text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60"
                  />
                </label>
              )}
            </>
          ) : mode === 'forgot' ? (
            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                <Mail className="h-3.5 w-3.5" /> Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60"
              />
            </label>
          ) : mode === 'signup' ? (
            <>
              <label className="block">
                <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                  <User className="h-3.5 w-3.5" /> Username
                </span>
                <div className="relative">
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value)
                      setUsernameState('idle')
                    }}
                    onBlur={handleUsernameBlur}
                    placeholder="twisties_fan"
                    autoComplete="username"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 pr-9 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60"
                  />
                  {usernameState === 'ok' && (
                    <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400" />
                  )}
                  {usernameState === 'taken' && (
                    <AlertCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-red-400" />
                  )}
                </div>
                {usernameState === 'taken' && (
                  <span className="mt-1 block text-xs text-red-400">
                    That username is already taken.
                  </span>
                )}
                <span className="mt-1 block text-xs text-amber-300">
                  3–24 characters — letters, numbers and underscores only.
                </span>
              </label>

              <label className="block">
                <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                  <Mail className="h-3.5 w-3.5" /> Email
                </span>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      setEmailState('idle')
                    }}
                    onBlur={handleEmailBlur}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 pr-9 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60"
                  />
                  {emailState === 'ok' && (
                    <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400" />
                  )}
                  {emailState === 'taken' && (
                    <AlertCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-red-400" />
                  )}
                </div>
                {emailState === 'taken' && (
                  <span className="mt-1 block text-xs text-red-400">
                    That email is already in use — try signing in instead.
                  </span>
                )}
              </label>
            </>
          ) : (
            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                <User className="h-3.5 w-3.5" /> Username or email
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="twisties_fan"
                autoComplete="username"
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60"
              />
            </label>
          )}

          {mode !== 'forgot' && mode !== 'confirm-code' && (
            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                <Lock className="h-3.5 w-3.5" /> Password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60"
              />
            </label>
          )}

          {mode === 'signup' && (
            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                <Lock className="h-3.5 w-3.5" /> Confirm password
              </span>
              <div className="relative">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  className={cn(
                    'w-full rounded-lg border bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60',
                    confirmPassword.length > 0 && confirmPassword !== password
                      ? 'border-red-400/50'
                      : 'border-white/10'
                  )}
                />
                {confirmPassword.length > 0 && confirmPassword === password && (
                  <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400" />
                )}
              </div>
              {confirmPassword.length > 0 && confirmPassword !== password && (
                <span className="mt-1 block text-xs text-red-400">Passwords do not match.</span>
              )}
            </label>
          )}

          {error && (
            <div className="rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}
          {notice && (
            <div className="rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-300">
              {notice}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className={cn(
              'w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black transition-colors',
              busy ? 'cursor-not-allowed opacity-60' : 'hover:bg-amber-400'
            )}
          >
            {busy
              ? 'Please wait…'
              : mode === 'signin'
                ? 'Sign in'
                : mode === 'signup'
                  ? 'Create account'
                  : mode === 'confirm-code'
                    ? codeSent
                      ? 'Confirm email'
                      : 'Send code'
                    : 'Send remote login link'}
          </button>

          {mode === 'confirm-code' && (
            <div className="space-y-1.5 pt-1 text-center text-sm">
              {resendDone ? (
                <div className="text-xs text-emerald-300">
                  {codeSent
                    ? 'Fresh code sent — check your inbox (and spam folder).'
                    : 'Fresh confirmation email sent — check your inbox (and spam folder).'}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendBusy}
                  className="text-white/60 transition-colors hover:text-amber-300 disabled:opacity-60"
                >
                  {resendBusy ? 'Sending…' : 'Didn\u2019t get the email? Resend confirmation'}
                </button>
              )}
            </div>
          )}

          {mode === 'signin' && (
            <div className="space-y-1.5 pt-1 text-center text-sm">
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setMode('forgot')
                    setError(null)
                    setNotice(null)
                  }}
                  className="text-white/60 transition-colors hover:text-amber-300"
                >
                  Forgot password?
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setMode('confirm-code')
                    setConfirmEmail('')
                    setCode('')
                    setCodeSent(false)
                    setError(null)
                    setNotice(null)
                  }}
                  className="text-white/60 transition-colors hover:text-amber-300"
                >
                  Email not confirming? <span className="font-medium text-amber-300">Enter backup code</span>
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setMode(SIGNUP_DISABLED ? 'signin' : 'signup')
                    setError(SIGNUP_DISABLED ? SIGNUP_DISABLED_MESSAGE : null)
                    setNotice(null)
                  }}
                  className="text-white/60 transition-colors hover:text-amber-300"
                >
                  New here? <span className="font-medium text-amber-300">Create an account</span>
                </button>
              </div>
            </div>
          )}

          {mode === 'signin' && (
            <>
              <div className="flex items-center gap-3 pb-3 pt-2">
                <span className="h-px flex-1 bg-white/10" />
                <span className="text-xs uppercase tracking-wide text-white/40">or</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <button
                type="button"
                onClick={handleGoogleButton}
                disabled={false}
                aria-label={googleBusy ? 'Cancel Google sign-in' : 'Sign in with Google'}
                title={googleBusy ? 'Tap to cancel and try again' : undefined}
                className={cn(
                  'flex w-full items-center justify-center gap-2.5 rounded-lg border border-white/15 bg-white px-4 py-2.5 text-sm font-semibold text-[#1f1f1f] transition-colors',
                  googleBusy ? 'cursor-pointer opacity-80 hover:bg-white/70' : 'hover:bg-white/90'
                )}
              >
                {googleBusy ? (
                  'Connecting… (tap to cancel)'
                ) : (
                  <>
                    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="#4285F4"
                        d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82Z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24Z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a11.99 11.99 0 0 0 0 10.76l3.98-3.09Z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z"
                      />
                    </svg>
                    Sign in with Google
                  </>
                )}
              </button>
            </>
          )}
        </form>
        )}


        <div className="mt-4 space-y-2 text-center text-sm">
          {mode !== 'signin' && (
            <button
              onClick={() => {
                setMode('signin')
                setError(null)
                setNotice(null)
              }}
              className="text-white/60 transition-colors hover:text-amber-300"
            >
              {mode === 'forgot' ? 'Back to sign in' : 'Already have an account? '}
              {mode === 'signup' && <span className="font-medium text-amber-300">Sign in</span>}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
